import _ from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import { initDycolor, permissions } from '~';
import { CAMPAIGN_TYPES, FLOWS, MODES } from '@/util/constants';
import LandingPage from '@/components/rabbit/landing-page/LandingPage.vue';
import FacebookPage from '@/components/rabbit/facebook-page/FacebookPage.vue';
import FacebookPixel from '@/components/facebook-pixel/FacebookPixel.vue';
import CampaignStartDate from '@/components/rabbit/campaign-start-date/CampaignStartDate.vue';

initDycolor('campaign-summary', require(`${process.env.DYCOLOR_REQUIRE}./CampaignSummary.scss`)); // eslint-disable-line

const createField = (name, caption, dataFn, filterFn = () => true, component, permission) => ({
  name,
  caption,
  data: dataFn,
  filterFn,
  component,
  permission,
});

const availableFields = [
  createField('customer.remote_id', 'customer_field.title.remote_id',
    (c, t) => t(c.customer.remote_id),
    c => c.customer.remote_id !== null,
  ),
  createField('customer.name', 'campaign_field.title.customer-name',
    (c, t) => t(c.customer.name),
  ),
  createField('creator.email', 'campaign_field.title.creator',
    (c, t) => t(c.creator.email),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('budget', 'campaign_field.title.budget',
    (c, t) => {
      let budget = _.get(c, 'campaign_rule.budget_client', 0);
      if (!Number(budget)) {
        budget = _.get(_.get(c, 'details', []).find(d => d.name === 'budget'), 'value', 0);
      }
      return t('campaign_field.format.budget', { budget });
    },
  ),
  createField('objective', 'campaign_field.title.goal_type',
    (c, t) => t('campaign_field.format.goal_type', { goal_type: _.get(c, 'campaign_rule.goal_type', 0) }),
  ),
  createField('mode', 'campaign_field.title.mode',
    (c, t) => t('campaign_field.format.mode', { mode: c.mode }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('mode', 'campaign_field.title.period-count',
    (c, t) => t(c.period_count),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL && c.mode === MODES.subscription && c.period_count,
  ),
  createField('target.locations', 'campaign_field.title.locations',
    (c, t) => c.target.locations
      .map(l => t('campaign_field.format.location', { name: l.name, type: l.type }))
      .join(', '),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.radius', 'campaign_field.title.radius',
    (c, t) => t('campaign_field.format.radius', {
      radius: c.target.radius,
      unit: c.target.distance_unit,
    }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.gender', 'campaign_field.title.gender',
    (c, t) => t('campaign_field.format.gender', { genders: c.target.genders }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.age', 'campaign_field.title.age',
    (c, t) => t('campaign_field.format.age', { from: c.target.ages.from, to: c.target.ages.to }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.interests', 'campaign_field.title.interest',
    (c, t, e) => c.target.interests
      .map(id => {
        const interest = e.interests.find(i => i.id === id);
        if (interest) {
          const key = `terms.interests.${interest.slug}.name`;
          const translation = t(key);
          return key === translation ? interest.name : translation;
        }
        return null;
      })
      .filter(i => i)
      .join(', ')
      || t('terms.interest.anything'),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.placement', 'campaign_field.title.placement',
    (c, t) => t(`terms.placement.facebook${c.target.use_instagram ? '-instagram' : ''}`),
  ),
  createField('business_category', 'campaign_field.title.business-category',
    (c, t, e) => _.get(c.business_category, `translations.${e.interfaceLanguage}.name`, _.get(c.business_category, 'name', '')),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL && c.business_category !== null,
  ),
  createField('format', 'campaign_field.title.format',
    (c, t) => {
      let type = t('campaign_field.format.format', { format: c.type });
      if (
        Object.prototype.hasOwnProperty.call(c, 'details')
        && Object.prototype.hasOwnProperty.call(c.details, 'extraCostFormat')
        && Number(c.details.extraCostFormat)
      ) {
        type += ` ${t('campaign_field.format.format-extracost', { cost: c.details.extraCostFormat })}`;
      }
      return type;
    },
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('reach.guaranteed', 'campaign_field.title.guaranteed-reach',
    (c, t) => t('campaign_field.format.potential-reach', { reach: _.get(c, 'campaign_rule.min_reach', 0) }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('campaign.clone_of', 'campaign_field.title.clone_of',
    (c) => c.clone_of,
    (c) => c.clone_of !== null,
  ),
  createField('campaign.clone_for', 'campaign_field.title.clone_for',
    (c) => c.clone_for,
    (c) => c.clone_for !== null,
  ),
  createField('facebook_page', 'campaign_field.title.facebook-page', () => true, () => true, 'facebook-page', 'updateFacebookPage'),
  createField('landing_page', 'campaign_field.title.landing-page', () => true, () => true, 'landing-page', 'updateLandingPage'),
  createField('start_date', 'campaign_field.title.start-date', () => true, () => true, 'campaign-start-date', 'updateStartDate'),
  createField('facebook_pixel', 'campaign_field.title.facebook_pixel',
    c => _.get(c, 'facebook_pixel'),
    c => _.get(c, 'facebook_pixel.pixel_id'),
  ),
];

export default {
  components: {
    FacebookPixel,
    'landing-page': LandingPage,
    'facebook-page': FacebookPage,
    'campaign-start-date': CampaignStartDate,
  },
  computed: {
    ...mapGetters('campaign', { campaign: 'currentCampaign' }),
    ...mapState('interests', ['interests']),
    ...mapState('genericConfig', ['config']),
    ...mapState('i18n', ['interfaceLanguage']),
    showFields() {
      let fields = availableFields.filter(f => f.filterFn(this.campaign)).map(f => ({
        slug: f.name,
        name: this.$t(f.caption),
        value: f.data(this.campaign, this.$t.bind(this), this),
        component: f.component || null,
        permission: f.permission || null,
      }));
      let summary = _.get(this.config, 'rabbit_campaign_summary_fields', null);

      if (summary.length > 1) {
        summary = JSON.parse(summary);
        fields = fields.filter(i => _.find(summary, { key: i.slug }));
        fields = _.sortBy(fields, (item) => _.findIndex(summary, { key: item.slug }));
      }
      return fields;
    },
    isASAP() {
      return _.get(this.campaign, 'status_flow.tag', null) === FLOWS.ASAP;
    },
    permissions() {
      return permissions.getPermissions(this.campaign);
    },
  },
  methods: {
    ...mapActions('interests', ['getInterests']),
  },
  mounted() {
    if (!this.interests.length) {
      this.getInterests();
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/rabbit/campaign-summary/CampaignSummary.js